import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  ArrowLeft,
  ExternalLink,
  Github,
  Share2,
  Calendar,
  Eye,
  ThumbsUp,
  MessageCircle,
  Star,
} from "lucide-react";
import { useData } from "@/contexts/DataContext";
import DomPurifyContent from "@/utils/domPurifyContent";
import { ProjectDetailSkeleton } from "@/components/ui/ProjectDetailSkeleton";

const PortfolioDetail = () => {
  const { id } = useParams();
  const { getProjectById, projects } = useData();
  const [project, setProject] = useState(null);
  const [activeImageIndex, setActiveImageIndex] = useState(0);

  // Fetch single project
  useEffect(() => {
    const loadProject = async () => {
      if (id) {
        const projectData = await getProjectById(id);
        setProject(projectData);
      }
    };
    loadProject();
  }, [id, getProjectById]);

  // if (!project) return <p className="text-center py-20">Loading...</p>;
  if (!project) {
    return <ProjectDetailSkeleton />;
  }

  // Related projects from global projects list
  const relatedProjects = projects
    ?.filter(
      (p) => p?._id !== project?._id && p?.category === project?.category
    )
    .slice(0, 3);

  // Share function
  const shareProject = () => {
    if (navigator.share) {
      navigator.share({
        title: project.title,
        text: project.description,
        url: window.location.href,
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert("Project URL copied to clipboard!");
    }
  };

  return (
    <div className="min-h-screen py-12">
      <div className="container mx-auto px-4">
        {/* Back Navigation */}
        <div className="mb-8">
          <Button variant="ghost" asChild>
            <Link to="/portfolio">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Portfolio
            </Link>
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Project Header */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4 flex-wrap">
                  <Badge variant="outline">{project.category}</Badge>
                  <Badge
                    className={`${project.status === "Completed"
                        ? "bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300"
                        : "bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300"
                      }`}
                  >
                    {project.status}
                  </Badge>
                  {project.featured && (
                    <Badge className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300">
                      <Star className="w-3 h-3 mr-1" />
                      Featured
                    </Badge>
                  )}
                </div>
                <Button variant="ghost" size="icon" onClick={shareProject}>
                  <Share2 className="h-4 w-4" />
                </Button>
              </div>

              <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold">
                {project.title}
              </h1>
              {/* <DomPurifyContent className="text-xl text-muted-foreground" content={project?.description} /> */}

              {/* Project Actions */}
              <div className="flex flex-col sm:flex-row gap-4">
                {project.liveUrl && (
                  <Button asChild className="bg-purple-600 hover:bg-purple-700">
                    <a
                      href={project.liveUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <ExternalLink className="mr-2 h-4 w-4" />
                      View Live
                    </a>
                  </Button>
                )}
                {project.githubUrl && (
                  <Button variant="outline" asChild>
                    <a
                      href={project.githubUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Github className="mr-2 h-4 w-4" />
                      View Code
                    </a>
                  </Button>
                )}
              </div>
            </div>

            {/* Project Images */}
            <div className="space-y-4 ">
              <div className="aspect-video rounded-lg overflow-hidden w-full">
                <img
                  src={project.images[activeImageIndex]?.url}
                  alt={`${project.title} - Image ${activeImageIndex + 1}`}
                  className="w-full h-full object-cover"
                />
              </div>

              {project.images.length > 1 && (
                <div className="flex gap-2 overflow-x-auto pb-2">
                  {project?.images?.map((image, index) => (
                    <button
                      key={index}
                      onClick={() => setActiveImageIndex(index)}
                      className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-colors ${activeImageIndex === index
                          ? "border-purple-600"
                          : "border-transparent hover:border-gray-300"
                        }`}
                    >
                      <img
                        src={image?.url}
                        alt={`Thumbnail ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Project Details */}
            <Card>
              <CardContent className="p-2 lg:p-8">
                <h2 className="text-2xl font-semibold mb-6">
                  Project Overview
                </h2>
                {/* <DomPurifyContent content={project?.description} /> */}
                <div className="prose">
                  <DomPurifyContent content={project?.description} />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-8">
            {/* Project Info */}
            <Card>
              <CardContent className="p-6">
                <h3 className="font-semibold mb-4">Project Information</h3>
                <div className="space-y-4">
                  <div>
                    <div className="text-sm text-muted-foreground mb-1">
                      Category
                    </div>
                    <div className="font-medium">{project.category}</div>
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground mb-1">
                      Status
                    </div>
                    <div className="font-medium">{project.status}</div>
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground mb-1">
                      Technologies
                    </div>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {project.technologies?.map((tech, idx) => (
                        <Badge
                          key={idx}
                          variant="secondary"
                          className="text-xs"
                        >
                          {tech}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Project Stats */}
            <Card>
              <CardContent className="p-6">
                <h3 className="font-semibold mb-4">Project Stats</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Eye className="w-4 h-4 text-muted-foreground" />
                      <span className="text-sm">Views</span>
                    </div>
                    <span className="font-medium">
                      {Math.floor(Math.random() * 500) + 100}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm sm:text-base">
                    <div className="flex items-center gap-2">
                      <ThumbsUp className="w-4 h-4 text-muted-foreground" />
                      <span className="text-sm">Likes</span>
                    </div>
                    <span className="font-medium">
                      {Math.floor(Math.random() * 50) + 10}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <MessageCircle className="w-4 h-4 text-muted-foreground" />
                      <span className="text-sm">Comments</span>
                    </div>
                    <span className="font-medium">
                      {Math.floor(Math.random() * 20) + 5}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-muted-foreground" />
                      <span className="text-sm">Duration</span>
                    </div>
                    <span className="font-medium">
                      {Math.floor(Math.random() * 6) + 2} months
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Related Projects */}
            {relatedProjects.length > 0 && (
              <Card>
                <CardContent className="p-6">
                  <h3 className="font-semibold mb-4">Related Projects</h3>
                  <div className="space-y-4">
                    {relatedProjects.map((relatedProject) => (
                      <Link
                        key={relatedProject._id}
                        to={`/portfolio/${relatedProject._id}`}
                        className="block p-3 rounded-lg border hover:bg-muted/50 transition-colors"
                      >
                        <div className="flex gap-3">
                          <img
                            src={relatedProject.images[0]?.url}
                            alt={relatedProject.title}
                            className="w-12 h-12 sm:w-16 sm:h-16 rounded object-cover flex-shrink-0"
                          />
                          <div className="flex-1 min-w-0">
                            <h4 className="font-medium text-sm truncate">
                              {relatedProject.title}
                            </h4>
                            <DomPurifyContent
                              className="text-xs text-muted-foreground mt-1 line-clamp-2"
                              content={relatedProject.description}
                            />
                            {/* <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{relatedProject.description}</p> */}
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Contact CTA */}
            <Card className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-950/20 dark:to-purple-900/20">
              <CardContent className="p-6 text-center">
                <h3 className="font-semibold mb-2">
                  Interested in Similar Work?
                </h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Let's discuss your project requirements and how I can help
                  bring your ideas to life.
                </p>
                <Button
                  size="sm"
                  asChild
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  <Link to="/contact">Get In Touch</Link>
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PortfolioDetail;
